import React from 'react';
import {useFormContext} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useMatch} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {SectionWithBorderField} from '@edna/components';

import {TChannelType} from 'src/constants';
import {ExternalLinkIcon} from 'src/icons';
import PATHS from 'src/paths';

import {useGetAvailableActiveTariffsQuery, useGetAvailableTariffQuery} from '../api';
import * as S from './style';

type TProps = {
  channelType: TChannelType;
  name: string;
  readOnly?: boolean;
  disabled?: boolean;
};

export const BaseTariffSelect = React.memo<TProps>(({name, channelType, disabled, readOnly}) => {
  const {t} = useTranslation();

  const {watch, setValue} = useFormContext();
  const tariffId = watch(name);
  const canSelect = !readOnly && !disabled;

  const match = useMatch({path: PATHS.SUPPORT_CHANNEL_VIEW, end: false});
  const tenantId = match?.params.tenantId;

  const {data: tariffs = [], fulfilledTimeStamp} = useGetAvailableActiveTariffsQuery(channelType, {
    skip: !canSelect,
  });
  const {data: selectedTariff} = useGetAvailableTariffQuery(
    tariffId ? {id: tariffId, tenantId} : skipToken,
    {skip: canSelect},
  );

  const options = React.useMemo(
    () =>
      selectedTariff
        ? [{value: selectedTariff.id, label: selectedTariff.name}]
        : tariffs.map((tariff) => ({value: tariff.id, label: tariff.name})),
    [fulfilledTimeStamp, selectedTariff],
  );

  React.useEffect(() => {
    if (canSelect && tariffs.length === 1) {
      setValue(name, tariffs[0].id, {shouldDirty: true});
    }
  }, [fulfilledTimeStamp, canSelect]);

  return (
    <SectionWithBorderField
      name={name}
      readOnly={disabled || readOnly}
      label={t('Tariffs:tariffSelect.label')}
      hint={
        !readOnly && (
          <Trans i18nKey="Tariffs:tariffSelect.hint">
            <S.Link href={PATHS.COMPANY_PROFILE_TARIFFS} target="_blank" />
            <S.Icon as={ExternalLinkIcon} size="16px" color="grey900" />
          </Trans>
        )
      }
    >
      <S.SelectField
        name={name}
        noMargin
        options={options}
        placeholder={t('Tariffs:tariffSelect.placeholder')}
        readOnly={readOnly}
        disabled={disabled}
      />
    </SectionWithBorderField>
  );
});

BaseTariffSelect.displayName = 'BaseTariffSelect';
